import React, { useState, useEffect, Children } from 'react';
import { Link } from 'react-router-dom';
import { useAdminEdit } from '../contexts/AdminEditContext';
import { Edit2, Check, X, Palette } from 'lucide-react';

const EditableButton = ({ 
  children,
  textKey,
  bgColorKey,
  textColorKey,
  hoverBgColorKey,
  hoverTextColorKey,
  defaultText,
  defaultBgColor = '#ffffff',
  defaultTextColor = '#1e40af',
  defaultHoverBgColor = '#f9fafb',
  defaultHoverTextColor = '#1e40af',
  className = '',
  onClick,
  to,
  type = 'button',
  ...props
}) => {
  const { isEditMode, isAdmin, saveCustomization, getCustomization } = useAdminEdit();
  const [isEditingText, setIsEditingText] = useState(false);
  const [isEditingColors, setIsEditingColors] = useState(false);
  const [editText, setEditText] = useState(children || defaultText);
  const [bgColor, setBgColor] = useState(getCustomization(bgColorKey, defaultBgColor));
  const [textColor, setTextColor] = useState(getCustomization(textColorKey, defaultTextColor));
  const [hoverBgColor, setHoverBgColor] = useState(getCustomization(hoverBgColorKey, defaultHoverBgColor));
  const [hoverTextColor, setHoverTextColor] = useState(getCustomization(hoverTextColorKey, defaultHoverTextColor));

  useEffect(() => {
    setEditText(children || getCustomization(textKey, defaultText));
    setBgColor(getCustomization(bgColorKey, defaultBgColor));
    setTextColor(getCustomization(textColorKey, defaultTextColor));
    setHoverBgColor(getCustomization(hoverBgColorKey, defaultHoverBgColor));
    setHoverTextColor(getCustomization(hoverTextColorKey, defaultHoverTextColor));
  }, [children, textKey, bgColorKey, textColorKey, hoverBgColorKey, hoverTextColorKey, getCustomization, defaultText, defaultBgColor, defaultTextColor, defaultHoverBgColor, defaultHoverTextColor]);

  // Get text value - if children is provided and is a string, use it, otherwise use customization or default
  const textValue = typeof children === 'string' ? children : (getCustomization(textKey, defaultText));
  const displayText = getCustomization(textKey, textValue);
  const displayBgColor = getCustomization(bgColorKey, defaultBgColor);
  const displayTextColor = getCustomization(textColorKey, defaultTextColor);
  const displayHoverBgColor = getCustomization(hoverBgColorKey, defaultHoverBgColor);
  const displayHoverTextColor = getCustomization(hoverTextColorKey, defaultHoverTextColor);
  
  // Check if children contains React elements (icons, etc.)
  const hasChildrenElements = children && (typeof children !== 'string') && (Children.count(children) > 0 || React.isValidElement(children));
  
  // Render content: if children has elements, render them with updated text spans, otherwise just text
  const renderContent = () => {
    if (hasChildrenElements) {
      // Map through children and replace text nodes with customized text
      return Children.map(children, (child) => {
        if (typeof child === 'string') {
          return displayText;
        }
        if (React.isValidElement(child) && child.type === 'span' && child.props.className?.includes('hidden')) {
          // This is likely the text span that should be customized
          return React.cloneElement(child, {
            children: displayText
          });
        }
        return child;
      });
    }
    return displayText;
  };

  const handleSaveText = () => {
    if (textKey) {
      saveCustomization(textKey, editText);
    }
    setIsEditingText(false);
  };

  const handleSaveColors = () => {
    if (bgColorKey) saveCustomization(bgColorKey, bgColor);
    if (textColorKey) saveCustomization(textColorKey, textColor);
    if (hoverBgColorKey) saveCustomization(hoverBgColorKey, hoverBgColor);
    if (hoverTextColorKey) saveCustomization(hoverTextColorKey, hoverTextColor);
    setIsEditingColors(false);
  };

  const ButtonComponent = to ? Link : 'button';
  const buttonProps = to ? { to } : { type, onClick };

  if (!isAdmin || !isEditMode) {
    return (
      <ButtonComponent
        {...buttonProps}
        {...props}
        className={className}
        style={{
          backgroundColor: displayBgColor,
          color: displayTextColor,
          ...props.style
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = displayHoverBgColor;
          e.target.style.color = displayHoverTextColor;
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = displayBgColor;
          e.target.style.color = displayTextColor;
        }}
      >
        {renderContent()}
      </ButtonComponent>
    );
  }

  if (isEditingText) {
    return (
      <div className="relative inline-block">
        <input
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          className="border-2 border-primary-500 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
          autoFocus
          style={{ color: '#111827' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSaveText();
            } else if (e.key === 'Escape') {
              setEditText(displayText);
              setIsEditingText(false);
            }
          }}
        />
        <div className="absolute -right-12 top-0 flex gap-1 z-20">
          <button
            onClick={handleSaveText}
            className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
            title="Save"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setEditText(displayText);
              setIsEditingText(false);
            }}
            className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (isEditingColors) {
    return (
      <div className="relative z-50">
        <div className="bg-white dark:bg-gray-800 border-2 border-primary-500 rounded-lg shadow-xl p-4 min-w-[280px]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">Button Colors</span>
            <button
              onClick={() => setIsEditingColors(false)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Background Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Text Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Hover Background</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={hoverBgColor}
                  onChange={(e) => setHoverBgColor(e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={hoverBgColor}
                  onChange={(e) => setHoverBgColor(e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Hover Text Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={hoverTextColor}
                  onChange={(e) => setHoverTextColor(e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={hoverTextColor}
                  onChange={(e) => setHoverTextColor(e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="p-2 border border-gray-200 dark:border-gray-700 rounded">
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Preview:</div>
              <button
                className="px-4 py-2 rounded text-sm font-medium transition-colors"
                style={{
                  backgroundColor: bgColor,
                  color: textColor
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = hoverBgColor;
                  e.target.style.color = hoverTextColor;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = bgColor;
                  e.target.style.color = textColor;
                }}
              >
                {displayText}
              </button>
            </div>

            <button
              onClick={handleSaveColors}
              className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              Save Colors
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group inline-block">
      <ButtonComponent
        {...buttonProps}
        {...props}
        className={className}
        style={{
          backgroundColor: displayBgColor,
          color: displayTextColor,
          ...props.style
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = displayHoverBgColor;
          e.target.style.color = displayHoverTextColor;
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = displayBgColor;
          e.target.style.color = displayTextColor;
        }}
      >
        {renderContent()}
      </ButtonComponent>
      <div className="absolute -top-1 -right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button
          onClick={() => setIsEditingText(true)}
          className="p-1 bg-primary-600 text-white rounded-full shadow-lg"
          title="Edit text"
        >
          <Edit2 className="w-3 h-3" />
        </button>
        <button
          onClick={() => setIsEditingColors(true)}
          className="p-1 bg-purple-600 text-white rounded-full shadow-lg"
          title="Edit colors"
        >
          <Palette className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default EditableButton;
